import { Link } from "react-router-dom";
import { Wrench, ArrowLeft } from "lucide-react";

function Section({ n, title, children }) {
  return (
    <section className="mt-7">
      <h2 className="font-display text-lg font-bold uppercase tracking-wide text-midnight">{n}. {title}</h2>
      <div className="mt-2 space-y-2 text-sm leading-relaxed text-slate-700">{children}</div>
    </section>
  );
}

function SubHead({ children }) {
  return <p className="mt-3 font-semibold text-midnight">{children}</p>;
}

function Bullets({ items }) {
  return (
    <ul className="ml-5 list-disc space-y-1">
      {items.map((t, i) => <li key={i}>{t}</li>)}
    </ul>
  );
}

export default function Privacy() {
  return (
    <div className="min-h-screen bg-background" data-testid="privacy-page">
      <header className="flex items-center gap-2.5 bg-midnight px-6 py-4">
        <Link to="/" data-testid="header-logo" className="flex items-center gap-2.5">
          <div className="flex h-8 w-8 items-center justify-center rounded-md bg-white/10"><Wrench className="h-4 w-4 text-brand" /></div>
          <span className="font-display text-[15px] font-bold tracking-widest text-white">PRO GARAGES</span>
        </Link>
      </header>

      <div className="mx-auto max-w-2xl px-4 py-10">
        <h1 className="font-display text-3xl font-bold uppercase text-midnight">Privacy Policy</h1>

        <div className="mt-6 rounded-xl border border-border bg-card p-6">
          <Section n={1} title="Introduction">
            <p>Pro Garages is an online garage-management software service operated by BIS AUTO BODYWORKS LTD.</p>
            <p>We take privacy seriously and aim to handle personal information responsibly, transparently and in accordance with applicable UK data protection law.</p>
            <p>This Privacy Policy explains how personal information is collected, used, stored, shared and protected when you visit our website, create an account, use our software or contact us.</p>
          </Section>

          <Section n={2} title="Who we are">
            <p>Business name: BIS AUTO BODYWORKS LTD</p>
            <p>Trading name: Pro Garages</p>
            <p>Address: Unit 7 Kartar Trading Estate, Shale Street, Bilston, United Kingdom, WV14 0HF</p>
            <p>Email: <a className="font-semibold text-brand-2" href="mailto:support@progarages.co.uk">support@progarages.co.uk</a></p>
            <p>Website: <a className="font-semibold text-brand-2" href="https://progarages.co.uk">https://progarages.co.uk</a></p>
            <p>Company number: 17193494</p>
            <p>For personal information relating to our own website visitors, account holders, subscription administration and support activities, BIS AUTO BODYWORKS LTD will generally act as the data controller.</p>
            <p>For personal information that a garage enters into Pro Garages about its own customers, employees or suppliers, the garage will generally act as the data controller and Pro Garages will generally act as its data processor when handling that information to provide the software.</p>
            <p>The precise roles depend on the processing activity and must be reflected in the applicable agreements.</p>
          </Section>

          <Section n={3} title="Personal information we may collect">
            <p>The information we handle depends on how you use Pro Garages.</p>
            <SubHead>A. Account and business information</SubHead>
            <p>This may include:</p>
            <Bullets items={[
              "Name and email address.",
              "Login and email-verification status.",
              "Garage or business name and contact details.",
              "Account roles and permissions.",
              "Subscription plan, trial status and billing history.",
              "Communications with our support team.",
            ]} />
            <SubHead>B. Customer and vehicle information entered by garages</SubHead>
            <p>Depending on the features used, a garage may enter:</p>
            <Bullets items={[
              "Customer names, telephone numbers, email addresses and addresses.",
              "Vehicle registration numbers and vehicle details.",
              "Booking dates, job descriptions and work history.",
              "Quotations, estimates, invoices and payment records.",
              "Vehicle condition reports, notes and photographs.",
              "Parts, supplier and inventory information.",
              "Staff assignments and related business records.",
            ]} />
            <p>The garage using the software is responsible for determining what information it enters and ensuring it has the necessary lawful basis to use that information.</p>
            <SubHead>C. Payment information</SubHead>
            <p>We may process subscription and payment-status information, such as the selected plan, transaction status and subscription dates.</p>
            <p>Payments may be processed by a third-party payment provider. The precise payment information we receive and retain depends on the provider&apos;s integration. We do not claim that payment-card information is never handled by our systems until the live payment flow has been verified.</p>
            <SubHead>D. Technical and security information</SubHead>
            <p>Depending on the actual configuration, we may collect technical information such as IP addresses, browser and device information, login events, security logs, error reports and cookie or similar technology data.</p>
            <p>The exact information collected must be confirmed through a technical review of the live website and app.</p>
          </Section>

          <Section n={4} title="How we use personal information">
            <p>We may use personal information for the following purposes:</p>
            <Bullets items={[
              "To create and administer accounts.",
              "To verify email addresses and secure access.",
              "To provide garage-management functions.",
              "To manage subscriptions and payments.",
              "To respond to support enquiries.",
              "To send service-related emails, such as booking notifications, account messages and document emails where requested.",
              "To maintain, troubleshoot and improve the service.",
              "To detect fraud, misuse, unauthorised access and security incidents.",
              "To maintain appropriate records and comply with legal obligations.",
              "To handle complaints and resolve disputes.",
            ]} />
            <p>We will not use personal information for a new, incompatible purpose without an appropriate legal basis and any required notice or consent.</p>
          </Section>

          <Section n={5} title="Our lawful bases for processing">
            <p>UK data protection law requires a lawful basis for processing personal information.</p>
            <p>Depending on the activity, our lawful bases may include:</p>
            <Bullets items={[
              "Contract: where processing is necessary to create an account, provide the subscribed service, administer a subscription or respond to a request made before entering into a contract.",
              "Legitimate interests: where processing is necessary for legitimate interests such as protecting the platform, preventing misuse, maintaining security or managing our business, provided those interests are not overridden by individuals' rights.",
              "Legal obligation: where we must retain or disclose information to comply with applicable law.",
              "Consent: where consent is the appropriate legal basis, such as for certain non-essential cookies or marketing activities where consent is required.",
            ]} />
            <p>We must confirm the lawful basis for each actual processing activity and explain it accurately in the final published policy.</p>
          </Section>

          <Section n={6} title="Garage customer data and responsibilities">
            <p>A garage may use Pro Garages to maintain information about its own customers, vehicles, bookings, work, quotations, invoices and payments.</p>
            <p>For these records, the garage will generally decide why the information is collected and how it is used. The garage is therefore generally responsible for:</p>
            <Bullets items={[
              "Having a lawful basis for collecting and using the information.",
              "Providing an appropriate privacy notice to its customers.",
              "Ensuring that information entered into the software is relevant and accurate.",
              "Controlling which employees can access the information.",
              "Responding to data protection requests concerning information it controls.",
            ]} />
            <p>Pro Garages processes this information to provide the software and related functions in accordance with the applicable agreement and documented instructions.</p>
            <p>Our Data Processing Agreement will set out the respective responsibilities in more detail.</p>
          </Section>

          <Section n={7} title="Who we share information with">
            <p>We may need to share information with service providers that support the operation of Pro Garages.</p>
            <p>These may include providers of:</p>
            <Bullets items={[
              "Application hosting and infrastructure.",
              "Databases and file storage.",
              "Payment processing.",
              "Email delivery.",
              "Vehicle registration or MOT-related lookup services.",
              "Technical monitoring, support or security services, if used.",
            ]} />
            <p>The actual provider list must be verified before publication. We will maintain a Third-Party Services and Subprocessors List identifying the providers we use, their purposes and relevant data-protection arrangements.</p>
            <p>We may also disclose information where required by law, to respond to lawful requests, or where reasonably necessary to protect the rights and security of our users and business.</p>
            <p>We do not sell personal information to advertisers.</p>
          </Section>

          <Section n={8} title="International transfers">
            <p>Some service providers may process personal information outside the United Kingdom.</p>
            <p>We must verify the locations of the providers used by the production service and determine whether any international transfers occur. Where a restricted transfer takes place, the appropriate legal transfer mechanism and safeguards must be in place.</p>
            <p>We will not claim that all information is stored in the UK unless this has been verified.</p>
          </Section>

          <Section n={9} title="How long we keep information">
            <p>We retain personal information only for as long as necessary for the purposes for which it is processed, taking account of legal obligations, security, disputes and legitimate business requirements.</p>
            <p>The exact retention periods for Pro Garages have not yet been confirmed. They must be established by inspecting the actual database, file storage, generated PDFs, photographs, support records, email logs, audit logs, exports and backup arrangements.</p>
            <p>Before publication, this section must be completed with the verified retention schedule, including what happens when a garage closes its account.</p>
            <p>We will not promise a particular deletion or backup-removal deadline until the relevant technical process has been verified.</p>
          </Section>

          <Section n={10} title="How we protect information">
            <p>We aim to use appropriate technical and organisational measures to protect personal information against unauthorised access, accidental loss, alteration and disclosure.</p>
            <p>The specific security measures used by the production service must be verified and documented before this policy describes them in detail.</p>
            <p>No internet-based service can guarantee absolute security. If a personal data breach occurs, we will assess it and take appropriate steps in accordance with applicable law and our contractual obligations.</p>
          </Section>

          <Section n={11} title="Your data protection rights">
            <p>Depending on the circumstances and applicable law, individuals may have the right to:</p>
            <Bullets items={[
              "Request access to their personal information.",
              "Ask for inaccurate information to be corrected.",
              "Request deletion of information.",
              "Request restriction of processing.",
              "Object to certain processing.",
              "Request data portability where applicable.",
              "Withdraw consent where processing relies on consent.",
              "Complain to the Information Commissioner's Office (ICO).",
            ]} />
            <p>These rights are not absolute in every situation. For example, some information may need to be retained to comply with legal obligations.</p>
            <p>To make a request relating to Pro Garages account information, contact <a className="font-semibold text-brand-2" href="mailto:support@progarages.co.uk">support@progarages.co.uk</a>.</p>
            <p>If your information was entered by a garage using our software, that garage may be the appropriate controller to contact. We will assist the garage with requests where required by our agreement and applicable law.</p>
          </Section>

          <Section n={12} title="Marketing communications">
            <p>We may send essential service communications, such as account verification, subscription notices and requested document or booking emails.</p>
            <p>Marketing communications will be handled separately and in accordance with applicable marketing and privacy rules. Where consent is required, it will be requested separately and will not be a condition of creating an account or using the service.</p>
            <p>You can use the unsubscribe mechanism provided in marketing emails where applicable.</p>
          </Section>

          <Section n={13} title="Cookies and similar technologies">
            <p>Our website and app may use cookies or similar technologies for essential functions such as secure login and remembering preferences. Other technologies, such as analytics, may be used if enabled.</p>
            <p>The exact cookies and technologies used must be audited before this policy is finalised.</p>
            <p>Where consent is required for non-essential cookies or similar technologies, they must not be activated before valid consent is given. Users should be able to reject non-essential technologies, adjust their preferences and change their choice later.</p>
            <p>See our Cookie Policy and cookie settings for further details.</p>
          </Section>

          <Section n={14} title="Account closure and deletion">
            <p>You may request closure of your Pro Garages account through the available account controls or by contacting <a className="font-semibold text-brand-2" href="mailto:support@progarages.co.uk">support@progarages.co.uk</a>.</p>
            <p>Account closure, subscription cancellation and data deletion are separate processes. Cancelling a subscription does not necessarily delete stored information, and deleting an account must not be assumed to cancel a payment-provider subscription unless the relevant process confirms it.</p>
            <p>We will handle data export and deletion in accordance with our Data Export, Retention and Deletion Policy, applicable legal obligations and the terms of the relevant service agreement.</p>
            <p>The actual export options and deletion timelines must be confirmed before publication.</p>
          </Section>

          <Section n={15} title="Complaints and the ICO">
            <p>If you have a concern about how we handle personal information, contact:</p>
            <p>Email: <a className="font-semibold text-brand-2" href="mailto:support@progarages.co.uk">support@progarages.co.uk</a></p>
            <p>Postal address: BIS AUTO BODYWORKS LTD, Unit 7 Kartar Trading Estate, Shale Street, Bilston, United Kingdom, WV14 0HF.</p>
            <p>We will review your concern and respond appropriately.</p>
            <p>You may also complain to the UK Information Commissioner&apos;s Office: <a className="font-semibold text-brand-2" href="https://ico.org.uk/make-a-complaint/">https://ico.org.uk/make-a-complaint/</a></p>
            <p>We will confirm and meet any applicable ICO data protection fee or registration requirements.</p>
          </Section>

          <Section n={16} title="Changes to this Privacy Policy">
            <p>We may update this policy to reflect changes in the service, our processing activities or applicable law.</p>
            <p>The latest version should be available on <a className="font-semibold text-brand-2" href="https://progarages.co.uk">https://progarages.co.uk</a>, with its effective date displayed. Where required, we will notify affected users of material changes.</p>
          </Section>

          <Section n={17} title="Contact us">
            <p>For questions about this Privacy Policy or personal information handled directly by Pro Garages, contact:</p>
            <p>BIS AUTO BODYWORKS LTD — Pro Garages</p>
            <p>Unit 7 Kartar Trading Estate, Shale Street, Bilston, United Kingdom, WV14 0HF</p>
            <p>Email: <a className="font-semibold text-brand-2" href="mailto:support@progarages.co.uk">support@progarages.co.uk</a></p>
          </Section>
        </div>

        <Link to="/" data-testid="privacy-back-home" className="mt-6 inline-flex items-center gap-1 text-sm font-semibold text-brand-2"><ArrowLeft className="h-4 w-4" />Back home</Link>
      </div>
    </div>
  );
}
